import { Card, CardContent } from '@/components/ui-custom/Card';
import { Button } from '@/components/ui-custom/Button';
import { FileImage, Eye, Trash2, Loader2, RefreshCw, MoreVertical } from 'lucide-react';
import { useState, useEffect, memo, useCallback } from 'react';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { deleteFloorPlanWithCascade } from '@/services/floorPlanService';
import { FloorPlanNameEditor } from './FloorPlanNameEditor';
import { FloorPlanReplaceModal } from './FloorPlanReplaceModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFloorPlanEditPermission } from '@/hooks/useFloorPlanEditPermission';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/lib/logger';
import { useFloorPlanThumbnail } from '@/hooks/useFloorPlanThumbnail';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';

interface FloorPlanCardProps {
  floorPlan: {
    id: string;
    name: string;
    file_path: string;
    file_type: string;
    created_at: string;
    processing_status: string;
    ai_processed: boolean;
    project_id: string;
    is_cad_file?: boolean;
  };
  onView: (id: string) => void;
  onDelete: (id: string) => void;
  onNameUpdate?: (id: string, newName: string) => void;
  onReplace?: (id: string) => void;
  refreshFloorPlans?: () => void;
  canWrite?: boolean;
}

const FloorPlanCardComponent: React.FC<FloorPlanCardProps> = ({
  floorPlan,
  onView,
  onDelete,
  onNameUpdate,
  onReplace,
  refreshFloorPlans,
  canWrite,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [localName, setLocalName] = useState(floorPlan.name);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [canDelete, setCanDelete] = useState(true);
  const [isCheckingPermission, setIsCheckingPermission] = useState(false);

  const isUsingClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const clerkAuth = useClerkAuth();

  const { data: permissionData } = useFloorPlanEditPermission(floorPlan.id);
  const {
    thumbnailUrl,
    isLoading: isThumbnailLoading,
    isCadFile,
  } = useFloorPlanThumbnail(floorPlan);

  // Determine effective write permission: explicit prop overrides hook result if provided.
  const effectiveCanWrite =
    typeof canWrite === 'boolean' ? canWrite : (permissionData?.canEdit ?? false);

  // Check deletion permission for Clerk users
  useEffect(() => {
    const checkDeletePermission = async () => {
      if (!isUsingClerk || !clerkAuth.userId) {
        // For non-Clerk users, allow deletion based on effectiveCanWrite
        setCanDelete(effectiveCanWrite);
        return;
      }

      setIsCheckingPermission(true);

      try {
        // Fetch the floor plan to check if current user is the creator
        const supabase = await getAuthenticatedSupabaseClient();
        const { data: floorPlanData, error } = await supabase
          .from('floor_plans')
          .select('*')
          .eq('id', floorPlan.id)
          .single();

        if (error) {
          logger.error('Error fetching floor plan for deletion check:', error);
          // If we can't fetch the floor plan, default to not allowing deletion
          setCanDelete(false);
          return;
        }

        // Check if the current user is the creator
        const isCreator =
          floorPlanData &&
          'created_by_clerk_id' in floorPlanData &&
          floorPlanData.created_by_clerk_id === clerkAuth.userId;
        setCanDelete(isCreator && effectiveCanWrite);

        if (!isCreator) {
          logger.log('User is not the creator of this floor plan, deletion disabled');
        }
      } catch (error) {
        logger.error('Error checking delete permission:', error);
        setCanDelete(false);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    checkDeletePermission();
  }, [floorPlan.id, isUsingClerk, clerkAuth.userId, effectiveCanWrite]);

  useEffect(() => {
    // Reset state when floor plan changes
    setImageLoaded(false);
    setImageError(false);
  }, [floorPlan.file_path, retryCount]);

  // Update local name when prop changes
  useEffect(() => {
    setLocalName(floorPlan.name);
  }, [floorPlan.name]);

  const handleDelete = async () => {
    // Double-check permission before deletion
    if (!canDelete) {
      toast.error(
        "You don't have permission to delete this floor plan. Only the floor plan creator can delete it.",
      );
      return;
    }

    if (
      !confirm(
        'Are you sure you want to delete this floor plan? This action cannot be undone and will delete all related fixtures, circuits, and controls.',
      )
    ) {
      return;
    }

    setIsDeleting(true);

    try {
      const { success, error } = await deleteFloorPlanWithCascade(
        floorPlan.id,
        floorPlan.file_path,
      );

      if (!success) {
        throw error;
      }

      toast.success('Floor plan and all related elements deleted successfully');
      onDelete(floorPlan.id);
    } catch (error) {
      logger.error('Error deleting floor plan:', error);
      toast.error('Failed to delete floor plan');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRetryImage = () => {
    setRetryCount(prev => prev + 1);
    setImageError(false);
  };

  const handleReplace = () => {
    setIsReplaceModalOpen(true);
    if (onReplace) {
      onReplace(floorPlan.id);
    }
  };

  const handleReplacementComplete = useCallback(async () => {
    try {
      // CRITICAL: Remove ALL cached queries related to this floor plan
      // This prevents ERR_INSUFFICIENT_RESOURCES on replaced floor plans
      // where old and new queries run simultaneously
      await Promise.all([
        // Floor plan base queries
        queryClient.removeQueries({ queryKey: queryKeys.floorPlan(floorPlan.id) }),

        // DWG/CAD metadata queries (created by useFloorPlanThumbnail)
        queryClient.removeQueries({
          queryKey: [...queryKeys.floorPlan(floorPlan.id), 'dwg-metadata-thumbnail'],
        }),
        queryClient.removeQueries({
          queryKey: [...queryKeys.floorPlan(floorPlan.id), 'cad-thumbnail-hq'],
        }),
        queryClient.removeQueries({
          queryKey: queryKeys.floorPlanMetadata(floorPlan.id),
        }),

        // Entity queries (fixtures, circuits, rooms, etc.)
        // These accumulate during floor plan usage
        queryClient.removeQueries({ queryKey: queryKeys.fixtures(floorPlan.id) }),
        queryClient.removeQueries({ queryKey: queryKeys.circuits(floorPlan.id) }),
        queryClient.removeQueries({ queryKey: queryKeys.controls(floorPlan.id) }),
        queryClient.removeQueries({ queryKey: queryKeys.rooms(floorPlan.id) }),
        queryClient.removeQueries({ queryKey: queryKeys.panels(floorPlan.id) }),
      ]);

      logger.log('Successfully cleared all cached queries for replaced floor plan:', floorPlan.id);
    } catch (removeError) {
      logger.warn('Failed to clear cached queries after replacement', removeError);
    }

    if (refreshFloorPlans) {
      try {
        await refreshFloorPlans();
      } catch (error) {
        logger.error('Failed to refresh floor plans after replacement:', error);
      }
    }
  }, [queryClient, floorPlan.id, refreshFloorPlans]);

  // For CAD files with PNG thumbnails, treat them as images for display
  const effectiveFileType = isCadFile && thumbnailUrl ? 'png' : floorPlan.file_type.toLowerCase();
  const isImageType =
    ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(effectiveFileType) ||
    (isCadFile && thumbnailUrl);
  const isPdfType = effectiveFileType === 'pdf';

  // Log for debugging
  useEffect(() => {
    if (isCadFile) {
      logger.log('CAD file thumbnail:', {
        floorPlanId: floorPlan.id,
        thumbnailUrl,
        isThumbnailLoading,
        isImageType,
      });
    }
  }, [isCadFile, floorPlan.id, thumbnailUrl, isThumbnailLoading, isImageType]);

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div
            className="h-32 bg-muted rounded-md flex items-center justify-center mb-2 overflow-hidden relative cursor-pointer"
            onClick={() => {
              onView(floorPlan.id);
              navigate(`/floor-plan?id=${floorPlan.id}`);
            }}
          >
            {isImageType && thumbnailUrl && !imageError && !isThumbnailLoading ? (
              <img
                src={thumbnailUrl}
                alt={floorPlan.name}
                className={`h-full w-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{
                  // Ensure high-quality rendering
                  imageRendering: 'crisp-edges' as React.CSSProperties['imageRendering'],
                  // Add subtle background for better visibility
                  backgroundColor: isCadFile ? '#f9f9f9' : 'transparent',
                }}
                onLoad={() => {
                  logger.log(`Image loaded successfully: ${floorPlan.name}`);
                  setImageLoaded(true);
                }}
                onError={e => {
                  logger.error(`Failed to load image: ${thumbnailUrl}`, e);
                  setImageError(true);
                }}
              />
            ) : null}

            {isPdfType && thumbnailUrl && !imageError && !isThumbnailLoading ? (
              <object
                data={thumbnailUrl}
                type="application/pdf"
                className="h-full w-full"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <FileImage className="h-8 w-8 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">PDF</span>
                </div>
              </object>
            ) : null}

            {((!isImageType && !isPdfType) || imageError || !imageLoaded || isThumbnailLoading) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {isThumbnailLoading ? (
                  <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                ) : (
                  <>
                    <FileImage className="h-10 w-10 text-muted-foreground" />
                    {isCadFile && <span className="text-xs text-muted-foreground mt-1">CAD</span>}
                    {imageError && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={e => {
                          e.stopPropagation();
                          handleRetryImage();
                        }}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="group">
            {effectiveCanWrite ? (
              <FloorPlanNameEditor
                floorPlanId={floorPlan.id}
                currentName={localName}
                onNameUpdated={newName => {
                  setLocalName(newName);
                  onNameUpdate?.(floorPlan.id, newName);
                }}
              />
            ) : (
              <span className="font-medium truncate" title={localName}>
                {localName}
              </span>
            )}
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Added {new Date(floorPlan.created_at).toLocaleDateString()}
          </p>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                onView(floorPlan.id);
                navigate(`/floor-plan?id=${floorPlan.id}`);
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>

            {effectiveCanWrite && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="px-2"
                      onClick={e => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleReplace}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Replace Floor Plan
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant="outline"
                  size="sm"
                  className={
                    canDelete
                      ? 'text-destructive hover:bg-destructive/10 hover:text-destructive'
                      : 'opacity-50 cursor-not-allowed'
                  }
                  disabled={isDeleting || !canDelete || isCheckingPermission}
                  title={
                    !canDelete
                      ? 'Only the floor plan creator can delete this floor plan'
                      : undefined
                  }
                  onClick={e => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <FloorPlanReplaceModal
        isOpen={isReplaceModalOpen}
        onClose={() => setIsReplaceModalOpen(false)}
        onReplacementComplete={handleReplacementComplete}
        floorPlan={floorPlan}
      />
    </>
  );
};

export const FloorPlanCard = memo(FloorPlanCardComponent);
